import './index.scss'
import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import LayoutPartner from '../../components/FeaturesComponent/layoutPartner'
import BaseTitle from '../../components/BasicComponent/BaseTitle'
import VehicleTypeSelected from '../../components/BasicComponent/VehicleTypeSelected'
import BaseInput from '../../components/BasicComponent/BaseInput'
import BaseButton from '../../components/BasicComponent/BaseButton'
import VehicleItem from '../../components/BasicComponent/VehicleItem'
import VehicleItemSkeletonList from '../../components/BasicComponent/VehicleItemSkeletonList'
import BasePopup from '../../components/BasicComponent/BasePopup'
import LoadingScreen from '../../components/BasicComponent/LoadingScreen'
import HomeBannerCache from '../../components/BasicComponent/HomeBannerCache'
import CardIconsLinkSupport from '../../components/FeatureComponent/CardIconsLinkSupport'
import { useAppUserContext } from '../../context/AppUserContext'
import { useTrafficFineVehicleContext } from '../../context/TrafficFineVehicleContext'
import useLoadHomeCache from '../../hooks/common/useLoadHomeCache'
import useRefreshTrafficFineVehicleListOnRouteEnter from '../../hooks/common/useRefreshTrafficFineVehicleListOnRouteEnter'
import appUserService from '../../services/appUserService'
import { PATH } from '../../constants/router'
import { OPTIONS_VEHICLE_TYPE_INFO_VEHICLE } from '../../constants/vehicle'
import { ERROR_CRIMINAL_MESSAGES, HAS_CRIMINAL } from '../../constants/criminal'
import { VEHICLE_TYPE } from '../../constants/global'
import { sanitizePlateInput, validatePlateNumber } from '../../helper/validatorPlateNumber'
import { LocalStorageManager } from '../../helper/localStorage'

const LAST_LOOKUP_VEHICLE_TYPE_STORAGE_KEY = 'trafficFineLookupV2VehicleType'

export default function AutoTrafficFineNotificationLookup() {
  const { homeBanners, homeCacheLoading } = useLoadHomeCache()
  const { userData } = useAppUserContext()
  const { vehicleList, isLoadingVehicleList, getVehicleLists } = useTrafficFineVehicleContext()
  
  const phoneNumberWithUsernamePrefix = useMemo(() => userData?.phoneNumberWithUsernamePrefix, [userData])
  const [vehicleType, setVehicleType] = useState(() => Number(LocalStorageManager.getItem(LAST_LOOKUP_VEHICLE_TYPE_STORAGE_KEY)) || VEHICLE_TYPE.CAR)
  const [vehicleIdentity, setVehicleIdentity] = useState('')
  const [expanded, setExpanded] = useState(false)
  const DEFAULT_VISIBLE = 3
  const [errorVehicleIdentity, setErrorVehicleIdentity] = useState('')
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [errorSubmit, setErrorSubmit] = useState(null)
  const history = useHistory()

  useRefreshTrafficFineVehicleListOnRouteEnter({
    isLoading: isLoadingVehicleList,
    refreshVehicleList: getVehicleLists
  })

  const onSubmit = async (vehicleIdentity, vehicleType, phoneNumber, vehicleListMerged) => {
    if (loadingSubmit) {
      return
    }
    const safeVehicleIdentity = String(vehicleIdentity || '').trim()

    if (!safeVehicleIdentity) {
      setErrorVehicleIdentity('Vui lòng nhập biển số xe!')
      return
    }

    const plateError = validatePlateNumber(safeVehicleIdentity)
    if (plateError) {
      setErrorVehicleIdentity(plateError)
      return
    }

    if (loadingSubmit) {
      return
    }

    const normalizedVehicleType = Number(vehicleType) || VEHICLE_TYPE.CAR
    const payload = {
      licensePlates: safeVehicleIdentity,
      vehicleType: normalizedVehicleType,
      phoneNumber
    }

    try {
      setLoadingSubmit(true)
      const response = await appUserService.PartnerCustomerCriminalRecordUserCheckViolation(payload, false)

      if (response.statusCode === 200) {
        const dataRes = response.data
        const criminalRecords = Array.isArray(dataRes?.criminalRecords) ? dataRes.criminalRecords : []

        if (dataRes?.hasCriminal === HAS_CRIMINAL.API_FAIL) {
          setErrorSubmit({
            title: 'Tra cứu không thành công',
            content: 'Đã xảy ra một vài sự cố kỹ thuật, vui lòng thử lại sau ít phút.'
          })
        } else {
          const vehicleActive =
            vehicleListMerged?.find(
              (vehicle) => vehicle.vehicleIdentity === safeVehicleIdentity && Number(vehicle.vehicleType) === normalizedVehicleType
            ) || {}

          if (dataRes?.hasCriminal === HAS_CRIMINAL.YES || dataRes?.hasCriminal === HAS_CRIMINAL.NO) {
            LocalStorageManager.setItem(LAST_LOOKUP_VEHICLE_TYPE_STORAGE_KEY, normalizedVehicleType)
            history.push({
              pathname: PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_CHECK_RESULT,
              state: {
                vehicleIdentity: safeVehicleIdentity,
                vehicleType: normalizedVehicleType,
                phoneNumber,
                isVehicleWarning: criminalRecords.length > 0 ? 1 : 0,
                checkedAt: new Date().toISOString(),
                criminalCount: criminalRecords.length,
                criminalData: criminalRecords,
                vehicleActivePackage: vehicleActive?.productId && vehicleActive
              }
            })
            void getVehicleLists()
          }
        }
      } else if (response?.error === 'INVALID_PLATE_NUMBER' || response?.error === 'VEHICLE_TYPE_MISMATCH') {
        setErrorVehicleIdentity('Biển số xe không hợp lệ, vui lòng kiểm tra lại.')
      } else if (ERROR_CRIMINAL_MESSAGES?.[response?.error]) {
        setErrorSubmit(ERROR_CRIMINAL_MESSAGES?.[response?.error])
      } else {
        setErrorSubmit({
          title: 'Tra cứu không thành công',
          content: 'Đã xảy ra một vài sự cố kỹ thuật, vui lòng thử lại.'
        })
      }
    } catch (error) {
      setErrorSubmit({
        title: 'Tra cứu không thành công',
        content: 'Đã xảy ra một vài sự cố kỹ thuật, vui lòng thử lại.'
      })
    } finally {
      setLoadingSubmit(false)
    }
  }

  const isLoadingScreenOpen = loadingSubmit || isLoadingVehicleList
  const shouldShowVehicleListSkeleton = isLoadingVehicleList
  const shouldShowVehicleList = !shouldShowVehicleListSkeleton && vehicleList.length > 0

  const canToggle = vehicleList.length > DEFAULT_VISIBLE
  const visibleVehicles = expanded ? vehicleList : vehicleList.slice(0, DEFAULT_VISIBLE)
  const hiddenCount = Math.max(vehicleList.length - DEFAULT_VISIBLE, 0)

  const goToPackageInfo = (vehicle) => {
    history.push({
      pathname: PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_PACKAGE_INFO,
      state: {
        vehicleIdentity: vehicle?.vehicleIdentity,
        vehicleType: vehicle?.vehicleType,
        vehicleActivePackage: vehicle
      }
    })
  }

  return (
    <LayoutPartner blendHeaderWithBody={true} scrollableTitle={'Tra cứu phạt nguội'}>

      <div className="AutoTrafficFineNotificationLookup">
        <div className="AutoTrafficFineNotificationLookup_infoVehicle">
          <div className="AutoTrafficFineNotificationLookup_infoVehicle_title">Thông tin xe</div>
          <VehicleTypeSelected
            options={OPTIONS_VEHICLE_TYPE_INFO_VEHICLE}
            value={vehicleType}
            onChange={(nextVehicleType) => setVehicleType(nextVehicleType)}
          />
          <div className="AutoTrafficFineNotificationLookup_infoVehicle_input">
            <BaseInput
              placeholder="Ví dụ: 29A12345"
              value={vehicleIdentity}
              onChange={(event) => {
                const value = sanitizePlateInput(event.target.value)
                setVehicleIdentity(value)
                setErrorVehicleIdentity('')
              }}
              showClear={Boolean(vehicleIdentity)}
              onClear={() => {
                setVehicleIdentity('')
                setErrorVehicleIdentity('')
              }}
              error={errorVehicleIdentity}
              buttonRight={
                <BaseButton
                  type="right"
                  onClick={() => onSubmit(vehicleIdentity, vehicleType, phoneNumberWithUsernamePrefix, vehicleList)}>
                  Kiểm tra
                </BaseButton>
              }
            />
          </div>
        </div>

        {shouldShowVehicleListSkeleton ? (
          <div className="AutoTrafficFineNotificationLookup_listVehicle">
            <BaseTitle>Xe của bạn</BaseTitle>
            <VehicleItemSkeletonList itemKeyPrefix="traffic-fine-lookup-vehicle-skeleton" />
          </div>
        ) : null}

        {shouldShowVehicleList && (
          <div className="AutoTrafficFineNotificationLookup_listVehicle">
            <BaseTitle>Xe của bạn ({vehicleList.length})</BaseTitle>
            {visibleVehicles.map((vehicle, index) => (
              <VehicleItem
                key={index}
                vehicleIdentity={vehicle?.vehicleIdentity}
                vehicleType={vehicle?.vehicleType}
                isRegisteredPackage={vehicle?.productId}
                notifyEndDateTime={vehicle?.notifyEndDateTime}
                registeredPackageMode={1}
                productName={vehicle?.productName}
                allowRenew={vehicle?.allowRenew}
                onClick={() => {
                  onSubmit(vehicle?.vehicleIdentity, vehicle?.vehicleType, phoneNumberWithUsernamePrefix, vehicleList)
                }}
                onClickArrowRight={() => {
                  onSubmit(vehicle?.vehicleIdentity, vehicle?.vehicleType, phoneNumberWithUsernamePrefix, vehicleList)
                }}
                onActionClick={() => {
                  if (vehicle?.productId) {
                    goToPackageInfo(vehicle)
                    return
                  }

                  history.push({
                    pathname: PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_SERVICES,
                    state: {
                      trafficFineRegistrationVehicle: {
                        vehicleIdentity: vehicle?.vehicleIdentity || undefined,
                        vehicleType: Number(vehicle?.vehicleType) || undefined
                      }
                    }
                  })
                }}
              />
            ))}
            {canToggle ? (
              <button type="button" className="traffic-fine-toggle-btn" onClick={() => setExpanded((v) => !v)}>
                <span>{expanded ? 'Thu gọn' : `Xem thêm${hiddenCount ? ` (${hiddenCount})` : ''}`}</span>
                <span className={`traffic-fine-toggle-arrow ${expanded ? 'is-expanded' : ''}`}>
                  <svg viewBox="0 0 12 6" fill="none">
                    <path d="M1 1L6 5L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
            ) : null}
          </div>
        )}

        <BasePopup
          open={!!errorSubmit}
          title={errorSubmit?.title}
          content={errorSubmit?.content}
          onClose={() => {
            setErrorSubmit(null)
          }}
        />
        <LoadingScreen open={isLoadingScreenOpen} />
        <CardIconsLinkSupport />
        <HomeBannerCache promoBanners={homeBanners} loading={homeCacheLoading} />

      </div>
    </LayoutPartner>
  )
}
